import { defineComponent as _defineComponent } from 'vue'
import { formattedDate } from '@/composables/utils';
import OddsEvent from './OddsEvent.vue';
import { PropType, computed, getCurrentInstance } from 'vue';
import {
  PreviewEventGroup,
  PreviewOutcome,
  PreviewEvent,
  PreviewOdd,
  HeaderSign
} from '../../../../../types/sport';
import { GenericOption } from '../../../../../types/components';
import OddsTitle from './OddsTitle.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'OddsEventsGroup',
  props: {
  eventsGroup: {
    type: Object as PropType<PreviewEventGroup>,
    required: true
  },
  marketName: {
    type: String,
    required: false
  },
  isFullScreen: {
    type: Boolean,
    required: false
  },
  selectedSpread: {
    type: Object as PropType<GenericOption>,
    required: false,
    default: undefined
  },
  spreadOptions: {
    type: Array as PropType<GenericOption[]>,
    required: false,
    default: () => []
  },
  sportId: {
    type: String,
    required: true
  },
  categoryId: {
    type: String,
    required: true
  },
  tournamentId: {
    type: String,
    required: true
  },
  title: {
    type: String,
    required: false
  },
  baseUrl: {
    type: String,
    required: false
  },
  loading: {
    type: Boolean,
    required: false,
    default: false
  },
  isPreviewMode: {
    type: Boolean,
    required: false,
    default: false
  },
  columns: {
    type: Array as PropType<string[]>,
    required: false,
    default: undefined
  }
},
  emits: ['spread-change', 'add-odd-to-cart'],
  setup(__props, { emit }) {

const props = __props;



const handleSpreadChange = (item: GenericOption) => {
  emit('spread-change', item);
};

// Accesso allo store per recuperare headerSign
const instance = getCurrentInstance();
const store = instance?.proxy?.$store;

// Computed per recuperare headerSign dallo store quando isPreviewMode è true
const headerSign = computed((): HeaderSign[] | undefined => {
  if (!props.isPreviewMode || !store) {
    return undefined;
  }
  return store.getters['IllinoisSport/getHeaderSignByTournament'](
    props.tournamentId
  );
});

// Helper per filtrare gli outcomes per mainLine (per uso nei computed)
const filterOutcomesByMainLineForComputed = (
  outcomes: PreviewOutcome[],
  odd: PreviewOdd
): PreviewOutcome[] => {
  if ((odd as any).odd_with_specifier === true) {
    const mainLine = (odd as any).mainLine;
    if (mainLine != null && mainLine !== '') {
      // Applica il filtro per mainLine
      const filteredOutcomes = outcomes.filter(outcome => {
        const specifier = outcome.specifier;
        return (
          specifier === null ||
          specifier === undefined ||
          specifier === '' ||
          specifier === mainLine ||
          mainLine === null
        );
      });

      // Se non sono state trovate outcomes valide dopo il filtro per mainLine,
      // recupera la prima outcome originale con status === 1 e filtra per il suo specifier
      if (filteredOutcomes.length === 0) {
        const firstOutcomeWithStatus1 = outcomes.find(
          outcome => outcome.status === 1
        );

        if (firstOutcomeWithStatus1 && firstOutcomeWithStatus1.specifier) {
          const fallbackSpecifier = firstOutcomeWithStatus1.specifier;
          return outcomes.filter(outcome => {
            const specifier = outcome.specifier;
            return (
              specifier === fallbackSpecifier ||
              specifier === null ||
              specifier === undefined ||
              specifier === ''
            );
          });
        }
      }

      return filteredOutcomes;
    } else {

        const firstOutcomeWithStatus1 = outcomes.find(
          outcome => outcome.status === 1
        );

        if (firstOutcomeWithStatus1 && firstOutcomeWithStatus1.specifier) {
          const fallbackSpecifier = firstOutcomeWithStatus1.specifier;
          return outcomes.filter(outcome => {
            const specifier = outcome.specifier;
            return (
              specifier === fallbackSpecifier ||
              specifier === null ||
              specifier === undefined ||
              specifier === ''
            );
        });
      }
      return outcomes;
    }
  }
  return outcomes;
};

const columns = computed(() => {
  // Se le colonne sono fornite come prop, usale direttamente
  if (
    props.columns &&
    Array.isArray(props.columns) &&
    props.columns.length > 0
  ) {
    return props.columns;
  }

  // Se isPreviewMode è true e headerSign è disponibile, usa headerSign per determinare le colonne
  if (props.isPreviewMode && headerSign.value) {
    const headerSignValue = headerSign.value;

    // Se headerSign.length === 1: le colonne sono gli outcomeNames di quel singolo headerSign
    if (headerSignValue.length === 1) {
      return headerSignValue[0].outcomeNames || [];
    }

    // Se headerSign.length > 1: le colonne sono i marketName di ogni headerSign nell'array
    // Ordina per il campo sort se presente
    const sortedHeaderSign = [...headerSignValue].sort(
      (a, b) => (a.sort || 0) - (b.sort || 0)
    );
    return sortedHeaderSign.map(h => h.marketName).filter(Boolean);
  }

  // Altrimenti calcola i nomi delle colonne da tutti gli eventi del gruppo
  const events = props.eventsGroup?.events || [];
  if (events.length === 0) {
    return [];
  }

  // Determina il tipo di struttura guardando il primo evento con odds valide
  let structureType: 'multiple-odds' | 'single-odd' | null = null;
  let firstEventWithOdds: PreviewEvent | null = null;

  for (const event of events) {
    if (event.odds && Array.isArray(event.odds) && event.odds.length > 0) {
      firstEventWithOdds = event;
      structureType = event.odds.length > 1 ? 'multiple-odds' : 'single-odd';
      break;
    }
  }

  if (!firstEventWithOdds || !structureType) {
    return [];
  }

  // Se odds.length > 1: i nomi delle colonne sono i marketName di ogni odd
  /*  if (structureType === 'multiple-odds') {
    const columnsSet = new Set<string>();
    const columnsOrder: string[] = [];

    // Prima passata: raccogli tutte le colonne mantenendo l'ordine del primo evento
    if (firstEventWithOdds.odds) {
      firstEventWithOdds.odds.forEach(odd => {
        const marketName = odd.marketName || '';
        if (marketName) {
          // Filtra gli outcomes per mainLine prima di verificare se ci sono outcomes
          const filteredOutcomes =
            odd.outcome && Array.isArray(odd.outcome)
              ? filterOutcomesByMainLineForComputed(
                  odd.outcome as PreviewOutcome[],
                  odd
                )
              : [];
          if (filteredOutcomes.length > 0 && !columnsSet.has(marketName)) {
            columnsSet.add(marketName);
            columnsOrder.push(marketName);
          }
        }
      });
    }

    // Seconda passata: aggiungi colonne da altri eventi che non sono già presenti
    events.forEach(event => {
      if (event.odds && Array.isArray(event.odds)) {
        event.odds.forEach(odd => {
          const marketName = odd.marketName || '';
          if (marketName) {
            // Filtra gli outcomes per mainLine prima di verificare se ci sono outcomes
            const filteredOutcomes =
              odd.outcome && Array.isArray(odd.outcome)
                ? filterOutcomesByMainLineForComputed(
                    odd.outcome as PreviewOutcome[],
                    odd
                  )
                : [];
            if (filteredOutcomes.length > 0 && !columnsSet.has(marketName)) {
              columnsSet.add(marketName);
              columnsOrder.push(marketName);
            }
          }
        });
      }
    });

    return columnsOrder;
  } */

  // Se odds.length === 1: i nomi delle colonne sono i nomi degli outcomes
  const firstOdd = firstEventWithOdds.odds[0];
  if (!firstOdd || !firstOdd.outcome || !Array.isArray(firstOdd.outcome)) {
    return [];
  }

  // Filtra gli outcomes per mainLine prima di calcolare le colonne
  const firstOutcomesFiltered = filterOutcomesByMainLineForComputed(
    firstOdd.outcome as PreviewOutcome[],
    firstOdd
  );

  const columnsSet = new Set<string>();
  const columnsOrder: string[] = [];

  // Prima passata: raccogli tutte le colonne mantenendo l'ordine del primo evento
  firstOutcomesFiltered.forEach(outcome => {
    const columnName = outcome.outcome_name || outcome.outcome_alias || '';
    if (columnName && !columnsSet.has(columnName)) {
      columnsSet.add(columnName);
      columnsOrder.push(columnName);
    }
  });

  // Seconda passata: aggiungi colonne da altri eventi che non sono già presenti
  events.forEach(event => {
    if (
      event.odds &&
      Array.isArray(event.odds) &&
      event.odds.length === 1 &&
      event.odds[0].outcome &&
      Array.isArray(event.odds[0].outcome)
    ) {
      // Filtra gli outcomes per mainLine prima di calcolare le colonne
      const outcomesFiltered = filterOutcomesByMainLineForComputed(
        event.odds[0].outcome as PreviewOutcome[],
        event.odds[0]
      );
      outcomesFiltered.forEach(outcome => {
        const columnName = outcome.outcome_name || outcome.outcome_alias || '';
        if (columnName && !columnsSet.has(columnName)) {
          columnsSet.add(columnName);
          columnsOrder.push(columnName);
        }
      });
    }
  });

  return columnsOrder;
});

const titleText = computed(() => {
  if (props.title) return props.title;
  return formattedDate((props.eventsGroup as any)?.scheduled as any);
});

// Helper per creare un outcome placeholder
const createPlaceholderOutcome = (outcomeName?: string): PreviewOutcome => {
  return {
    unique_id: '',
    outcome_id: 0,
    outcome_odd_value: '',
    status: 0, // Status 0 fa mostrare il lock
    name: '',
    outcome_alias: outcomeName || '',
    outcome_name: outcomeName || '',
    id_outcome: 0,
    ext_id: '',
    lastUpdate: '',
    sort: 0,
    alias: '',
    odd_percent: '',
    outcome_odd_value_for_order: '',
    active: false,
    specifier: ''
  };
};

// Helper per creare un odd placeholder
const createPlaceholderOdd = (marketName: string): PreviewOdd => {
  return {
    id: 0,
    unique_id: '',
    outcome: [],
    sort: 0,
    specifier: '',
    specifierSort: 0,
    marketName: marketName,
    multiplicity: '',
    multiplicityMin: 0,
    multiplicityMax: 0,
    odd_with_specifier: false
  };
};

// Calcola il numero massimo di outcomes per ogni marketName (dopo il filtro mainLine)
const maxOutcomesPerMarketName = computed(() => {
  const events = props.eventsGroup?.events || [];
  const maxOutcomesMap = new Map<string, number>();

  events.forEach(event => {
    if (event.odds && Array.isArray(event.odds) && event.odds.length > 1) {
      event.odds.forEach(odd => {
        const marketName = odd.marketName || '';
        if (marketName) {
          // Filtra gli outcomes per mainLine prima di contare
          const filteredOutcomes =
            odd.outcome && Array.isArray(odd.outcome)
              ? filterOutcomesByMainLineForComputed(
                  odd.outcome as PreviewOutcome[],
                  odd
                )
              : [];
          const outcomesCount = filteredOutcomes.length;
          const currentMax = maxOutcomesMap.get(marketName) || 0;
          if (outcomesCount > currentMax) {
            maxOutcomesMap.set(marketName, outcomesCount);
          }
        }
      });
    }
  });

  return maxOutcomesMap;
});

// Computed property che restituisce gli eventi con odds placeholder aggiunti
const eventsWithPlaceholders = computed(() => {
  const events = props.eventsGroup?.events || [];
  const columnsValue = columns.value;

  if (!columnsValue || columnsValue.length === 0) {
    return events;
  }

  // Se isPreviewMode è true e headerSign è disponibile, usa headerSign per determinare la struttura
  let structureType: 'multiple-odds' | 'single-odd' | null = null;
  let firstEventWithOdds: PreviewEvent | null = null;

  if (props.isPreviewMode && headerSign.value) {
    // Determina la struttura basandosi su headerSign
    structureType =
      headerSign.value.length === 1 ? 'single-odd' : 'multiple-odds';

    // Trova il primo evento con odds valide per applicare il filtro mainLine
    for (const event of events) {
      if (event.odds && Array.isArray(event.odds) && event.odds.length > 0) {
        firstEventWithOdds = event;
        break;
      }
    }
  } else {
    // Determina il tipo di struttura guardando il primo evento con odds valide
    for (const event of events) {
      if (event.odds && Array.isArray(event.odds) && event.odds.length > 0) {
        firstEventWithOdds = event;
        structureType = event.odds.length > 1 ? 'multiple-odds' : 'single-odd';
        break;
      }
    }
  }

  if (!structureType) {
    return events;
  }

  return events.map(event => {
    // Crea una copia dell'evento per non modificare l'originale
    const eventCopy: PreviewEvent = {
      ...event,
      odds: event.odds ? [...event.odds] : []
    };

    // Applica il filtro per mainLine a tutte le odds prima di creare i placeholder
    eventCopy.odds = eventCopy.odds.map(odd => {
      if (odd.outcome && Array.isArray(odd.outcome)) {
        const filteredOutcomes = filterOutcomesByMainLineForComputed(
          odd.outcome as PreviewOutcome[],
          odd
        );
        return {
          ...odd,
          outcome: filteredOutcomes
        };
      }
      return odd;
    });

    if (structureType === 'multiple-odds') {
      // Caso odds.length > 1: aggiungi odds placeholder per marketName mancanti
      const existingMarketNames = new Set<string>();
      eventCopy.odds.forEach(odd => {
        const marketName = odd.marketName || '';
        if (
          marketName &&
          odd.outcome &&
          Array.isArray(odd.outcome) &&
          odd.outcome.length > 0
        ) {
          existingMarketNames.add(marketName);
        }
      });

      // Se isPreviewMode è true e headerSign è disponibile, usa headerSign per determinare l'ordine
      let orderedColumns: string[] = columnsValue;
      if (
        props.isPreviewMode &&
        headerSign.value &&
        headerSign.value.length > 1
      ) {
        // Ordina headerSign per sort e usa i marketName nell'ordine corretto
        const sortedHeaderSign = [...headerSign.value].sort(
          (a, b) => (a.sort || 0) - (b.sort || 0)
        );
        orderedColumns = sortedHeaderSign
          .map(h => h.marketName)
          .filter(Boolean);
      }

      // Aggiungi odds placeholder nell'ordine corretto
      const oddsWithPlaceholders: PreviewOdd[] = [];
      const existingOddsMap = new Map<string, PreviewOdd>();
      eventCopy.odds.forEach(odd => {
        const marketName = odd.marketName || '';
        if (
          marketName &&
          odd.outcome &&
          Array.isArray(odd.outcome) &&
          odd.outcome.length > 0
        ) {
          existingOddsMap.set(marketName, odd);
        }
      });

      // Mantieni l'ordine delle colonne calcolate (o di headerSign se disponibile)
      orderedColumns.forEach(columnName => {
        if (existingOddsMap.has(columnName)) {
          oddsWithPlaceholders.push(existingOddsMap.get(columnName)!);
        } else {
          oddsWithPlaceholders.push(createPlaceholderOdd(columnName));
        }
      });

      eventCopy.odds = oddsWithPlaceholders;
    } else {
      // Caso odds.length === 1: aggiungi outcomes placeholder all'unico odd
      // Se isPreviewMode è true e headerSign è disponibile, usa outcomeNames da headerSign[0]
      let orderedOutcomeNames: string[] = columnsValue;
      if (
        props.isPreviewMode &&
        headerSign.value &&
        headerSign.value.length === 1
      ) {
        orderedOutcomeNames = headerSign.value[0].outcomeNames || columnsValue;
      }

      if (eventCopy.odds.length === 0) {
        // Se non c'è nessun odd, creane uno con outcomes come array vuoto
        eventCopy.odds = [
          {
            id: 0,
            unique_id: '',
            outcome: [],
            sort: 0,
            specifier: '',
            specifierSort: 0,
            marketName: '',
            multiplicity: '',
            multiplicityMin: 0,
            multiplicityMax: 0,
            odd_with_specifier: false
          }
        ];
      } else {
        // Aggiungi outcomes placeholder mancanti all'unico odd esistente
        const firstOdd = eventCopy.odds[0];
        if (firstOdd && firstOdd.outcome && Array.isArray(firstOdd.outcome)) {
          const existingOutcomeNames = new Set<string>();
          firstOdd.outcome.forEach(outcome => {
            const outcomeName =
              outcome.outcome_name || outcome.outcome_alias || '';
            if (outcomeName) {
              existingOutcomeNames.add(outcomeName);
            }
          });

          // Aggiungi outcomes placeholder nell'ordine corretto (da headerSign se disponibile)
          const outcomesWithPlaceholders: PreviewOutcome[] = [];
          const existingOutcomesMap = new Map<string, PreviewOutcome>();
          firstOdd.outcome.forEach(outcome => {
            const outcomeName =
              outcome.outcome_name || outcome.outcome_alias || '';
            if (outcomeName) {
              existingOutcomesMap.set(outcomeName, outcome);
            }
          });

          // Mantieni l'ordine delle colonne calcolate (o di headerSign se disponibile)
          orderedOutcomeNames.forEach(columnName => {
            if (existingOutcomesMap.has(columnName)) {
              outcomesWithPlaceholders.push(
                existingOutcomesMap.get(columnName)!
              );
            } else {
              outcomesWithPlaceholders.push(
                createPlaceholderOutcome(columnName)
              );
            }
          });

          eventCopy.odds[0] = {
            ...firstOdd,
            outcome: outcomesWithPlaceholders
          };
        }
      }
    }

    return eventCopy;
  });
});

const skeletonCount = computed(() => 3);
const emptyEvent = computed(
  () =>
    ({
      id: 0,
      home: '',
      away: '',
      scheduled: new Date().toISOString(),
      livescore: null,
      odds: [
        {
          outcome: Array((columns.value && columns.value.length) || 3).fill({}),
          unique_id: ''
        }
      ]
    } as any)
);

return { __sfc: true,props, emit, handleSpreadChange, instance, store, headerSign, filterOutcomesByMainLineForComputed, columns, titleText, createPlaceholderOutcome, createPlaceholderOdd, maxOutcomesPerMarketName, eventsWithPlaceholders, skeletonCount, emptyEvent, OddsEvent, OddsTitle }
}

})